import { LoginCallback } from "@okta/okta-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { OktaProvider } from "./auth/OktaProvider.js";
import { RequireAuth } from "./auth/RequireAuth.js";
import { SignIn } from "./pages/SignIn.js";
import { SignOut } from "./pages/SignOut.js";

/**
 * Shell root. Composition order matters:
 *  - The Router (main.tsx) wraps OktaProvider (OktaProvider's `restoreOriginalUri` uses
 *    `useNavigate`).
 *  - React Query owns server state; React Context (via Okta + useAuthContext) owns auth state.
 *
 * Each platform module is a code-split chunk loaded on first navigation into it, so the entry
 * bundle carries only the shell chrome + the welcome landing — admin's mapping editor, react-query
 * mutations, and tables don't ship until the operator visits `/admin/*`. This is what makes the
 * single-shell SPA viable without one monolithic initial download (Phase 2 goal: zero-reload
 * module switching without collapsing the initial-route budget).
 *
 * `<App>` assumes it is rendered inside a Router (BrowserRouter in main.tsx; MemoryRouter in tests).
 */
const CloseModule = lazy(() =>
  import("./modules/close/CloseModule.js").then((m) => ({ default: m.CloseModule })),
);
const AdminModule = lazy(() =>
  import("./modules/admin/AdminModule.js").then((m) => ({ default: m.AdminModule })),
);
const PlanningModule = lazy(() =>
  import("./modules/planning/PlanningModule.js").then((m) => ({ default: m.PlanningModule })),
);
const VaultModule = lazy(() =>
  import("./modules/vault/VaultModule.js").then((m) => ({ default: m.VaultModule })),
);
const Welcome = lazy(() => import("./pages/Welcome.js").then((m) => ({ default: m.Welcome })));

/** Retain bookmarked Close links that predate the platform welcome page. */
function PlatformIndex(): JSX.Element {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (params.has("step") || params.has("period")) {
    return <Navigate replace to={`/close${location.search}${location.hash}`} />;
  }
  return <Welcome />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

function ModuleFallback(): JSX.Element {
  return (
    <div role="status" aria-live="polite" className="p-8 text-aire-muted">
      Loading…
    </div>
  );
}

export function App(): JSX.Element {
  return (
    <OktaProvider>
      <QueryClientProvider client={queryClient}>
        <Routes>
          {/* Okta PKCE redirect target — the one shared platform callback (auth.md §5). */}
          <Route path="/auth/callback" element={<LoginCallback />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/signout" element={<SignOut />} />

          {/* Guarded module sections. RequireAuth runs once for the whole platform; the matched
              module then lazy-loads behind a Suspense boundary. */}
          <Route
            path="/*"
            element={
              <RequireAuth>
                <Suspense fallback={<ModuleFallback />}>
                  <Routes>
                    <Route path="/" element={<PlatformIndex />} />
                    <Route path="/admin/*" element={<AdminModule />} />
                    <Route path="/planning/*" element={<PlanningModule />} />
                    <Route path="/vault/*" element={<VaultModule />} />
                    <Route path="/close/*" element={<CloseModule />} />
                    <Route path="*" element={<Navigate replace to="/" />} />
                  </Routes>
                </Suspense>
              </RequireAuth>
            }
          />
        </Routes>
      </QueryClientProvider>
    </OktaProvider>
  );
}
